import type { WorkforceMember } from "@tdelivr/api-client/generated/identity/workforce-identity.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the staff board does to the members IAM-02 returns, away from any rendering.
 *
 * **This page's recorded blocker was the deepest on the console and it is now closed.** It read:
 *
 * > IAM-02 holds identity providers and subject links, not people: no name, no role and no last
 * > sign-in exists anywhere for a staff member. A listing of its links is route work and would
 * > leave all three of this board's columns with no source.
 *
 * Every clause of that was true. It was also the one blocker on this console that could not be
 * closed by publishing a route, because the noun itself was missing — no table, no column, no row.
 * [ADR 0084](../../../docs/adr/0084-a-staff-member-is-workforce-identity-and-nothing-held-one.md)
 * adds it to IAM-02 and argues why there rather than in a 143rd service.
 *
 * Two of the three designed columns are now real and the third is not:
 *
 * | column         | source                                                                   |
 * | -------------- | ------------------------------------------------------------------------ |
 * | `Person`       | `displayName`                                                            |
 * | `Role`         | `role` — the tenant's own words, `Dispatcher · Finance preparer`         |
 * | `Last sign-in` | **nothing a board can ask for.** See below                               |
 *
 * **`Last sign-in` is a near miss and that is what makes it worth stating.** IAM-04 holds device
 * sessions and `GET /v1/sessions` requires a `subjectId` — so a board could answer this column at
 * one request per row, which is the fan-out ADR 0047 declined EXP-005 over. Worse, it would answer
 * it for nobody on an `invited` row, because an invited member has no subject at all. The column
 * carries the absence and the detail panel carries the two instants IAM-02 does publish, which are
 * genuinely about this member rather than about their sessions: when they were invited, and when
 * they activated.
 *
 * **Three of the four designed row states are published and one is not.** `active`, `invite
 * pending` and `suspended` are IAM-02's three; `2-step missing` is whether a person has enrolled a
 * second factor, which is Keycloak's and not this platform's at all —
 * [ADR 0014](../../../docs/adr/0014-keycloak-as-the-token-issuer.md) puts credentials and their
 * policies with the token issuer. So the design's `M. Chege · Dispatcher · 12 days ago · caution ·
 * 2-step missing` row draws as `active` here, and the board says so under the grid rather than
 * silently showing a weaker state.
 *
 * **None of the four tiles has a source**, which is this console's established answer on all six of
 * its record boards rather than a new one. It is worth one extra sentence here because this is the
 * cheapest total on the platform: `Staff · 46` and `Invites pending · 3` are counts of rows in one
 * table with one index. R-04 still holds and ADR 0084's consequences section says why — a count
 * published on one listing and absent on seventy-six others leaves a reader unable to tell which
 * boards are showing them everything. `Without 2-step · 2` is the Keycloak fact above.
 */

/**
 * The state class and the word, from the member's own status.
 *
 * The three words are the design's own, from its rows: `active`, `invite pending`, `suspended`. The
 * classes are its own too — `done`, `waiting`, `closed` — read off the same rows.
 *
 * The design's fourth, `caution · 2-step missing`, is not produced by anything here. A member whose
 * second factor is missing is `active` as far as this platform can see, and inventing a fourth
 * state would mean this console deciding something only Keycloak knows.
 */
export function stateOf(member: WorkforceMember): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  switch (member.status) {
    case "active":
      return {
        classKey: "done",
        label: "active",
        title:
          "This person has signed in at least once and their account is linked. Whether they have enrolled a second factor is Keycloak's to say, so the design's 2-step missing state is not drawn here.",
      };
    case "invited":
      return {
        classKey: "waiting",
        label: "invite pending",
        title:
          "A name and a role are recorded and no account is linked yet. IAM-02 has no platform subject for this person until they sign in.",
      };
    case "suspended":
      return {
        classKey: "closed",
        label: "suspended",
        title:
          "This person can no longer be activated. IAM-02 publishes no way back from suspended — re-employing somebody is a new member with a new invitation — and their platform subject is kept so the audit trail that names it stays attributable.",
      };
  }
}

/** The states present in the rows fetched, in the design's own order, for the filter. */
export function statesPresent(
  members: readonly WorkforceMember[],
): readonly { readonly value: string; readonly label: string }[] {
  const order: readonly WorkforceMember["status"][] = ["active", "invited", "suspended"];
  return order
    .filter((status) => members.some((member) => member.status === status))
    .map((status) => {
      const example = members.find((member) => member.status === status)!;
      return { value: status, label: stateOf(example).label };
    });
}

/** One row of the staff board. */
export function memberRow(member: WorkforceMember): BoardRow {
  return {
    id: member.id,
    cells: [
      { text: member.displayName },
      { text: member.role },
      {
        /*
         * The design's `Last sign-in`, with `today 08:12`, `invited 2 days ago` and `12 days ago`
         * down it. IAM-04 holds sessions and its read is addressed by subject, so this column is
         * one request per row — and answers nothing at all for an invited member, who has no
         * subject. The two instants IAM-02 does hold are in the detail panel.
         */
        text: "",
        absent: true,
        title:
          "IAM-04 holds device sessions and its read requires a subject, so this column would be one request per row — and an invited member has no subject to ask about. When this person was invited and when they activated are in the detail panel.",
      },
    ],
    state: stateOf(member),
    detail: [
      { label: "Member", value: member.id, mono: true },
      { label: "Role", value: member.role },
      ...(member.platformSubject === undefined
        ? [
            {
              label: "Platform subject",
              /*
               * Not an absence: a genuine emptiness, and the one the state badge beside it is
               * about. An invited member has no subject because nobody has signed in as them.
               */
              value: "—",
            },
          ]
        : [{ label: "Platform subject", value: member.platformSubject, mono: true }]),
      ...(member.merchantId === undefined
        ? []
        : [{ label: "Merchant", value: member.merchantId, mono: true }]),
      ...(member.locationId === undefined
        ? []
        : [{ label: "Branch", value: member.locationId, mono: true }]),
    ],
    history: [
      { label: "Invited", value: asDay(member.invitedAt) },
      {
        label: "Activated",
        value: member.activatedAt === undefined ? "—" : asDay(member.activatedAt),
      },
      { label: "Last changed", value: asDay(member.updatedAt) },
      { label: "Version", value: `v${String(member.version)}` },
    ],
    glyph: "person",
  };
}

/** Whether a member belongs under the chosen state filter. `""` is unfiltered. */
export function matchesState(member: WorkforceMember, status: string): boolean {
  return status === "" || member.status === status;
}

/**
 * The scope this board spends, named once.
 *
 * `workforce:member:read`, and deliberately not `workforce:subject:resolve`, which is what this
 * page's `readRequirements` used to declare. Resolving a federated subject is what a service does
 * during sign-in; listing a tenant's staff is what an administrator does, and a sign-in path
 * granted this read could enumerate everybody. ADR 0084 §6.
 */
export const STAFF_READ_SCOPE = "workforce:member:read";

/**
 * Why the designed form cannot be sent, in the words the board shows on its refused action.
 *
 * Two reasons, and the second is the one that would survive a permission being granted: four of
 * the form's seven controls have nowhere to be stored, and the published body is `.strict()` so
 * each is a 400 rather than a value accepted and dropped.
 */
export const INVITE_REFUSAL =
  "This board holds workforce:member:read and not :write. The designed form also asks for a work email, a mobile, a profile photo and a 2-step requirement: the first three have no channel or store on this platform, and the fourth is Keycloak's — so the invitation IAM-02 accepts is a name, a role and a place.";
